import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';
import { IDLE_WARNING_THRESHOLD } from '../constants/dashboardConstants';
import { UseIdleWarningOptions, UseIdleWarningResult } from '../types';

/**
 * Hook to detect idle time and send Slack notifications
 * Tracks time since last task activity, not total clocked-in time
 */
export const useIdleWarning = ({
  isClockedIn,
  elapsedSeconds,
  currentTasks,
  intranetUser,
  username
}: UseIdleWarningOptions): UseIdleWarningResult => {
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [idleSeconds, setIdleSeconds] = useState(0);
  const idleWarningSentRef = useRef(false);
  const idleStartTimeRef = useRef<number | null>(null);

  // Check if any task is currently running
  const hasRunningTask = currentTasks?.some(task => task.isRunning) || false;

  // Build email from intranet user name
  const inferEmail = useCallback((name: string): string | null => {
    const nameParts = name.toLowerCase().split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[nameParts.length - 1]}@elinkdesign.com`;
    }
    return null;
  }, []);

  // Send idle warning via Slack
  const sendIdleSlackNotification = useCallback(async (idleMins: number) => {
    try {
      const userName = intranetUser?.name || username || '';
      const inferredEmail = inferEmail(userName);
      
      await axios.post(
        `${API_URL}/intranet/slack/idle-warning`,
        {
          employeeName: intranetUser?.name || username || 'Developer',
          idleMinutes: idleMins,
          email: inferredEmail
        },
        { withCredentials: true }
      );
    } catch (err) {
      console.error('Failed to send Slack notification:', err);
    }
  }, [intranetUser, username, inferEmail]);

  // Track idle time - reset when a task starts running
  useEffect(() => {
    if (!isClockedIn) {
      // Not clocked in - reset everything
      idleStartTimeRef.current = null;
      setIdleSeconds(0);
      idleWarningSentRef.current = false;
      setShowIdleWarning(false);
      return;
    }

    if (hasRunningTask) {
      // Task is running - reset idle tracking
      idleStartTimeRef.current = null;
      setIdleSeconds(0);
      idleWarningSentRef.current = false;
      setShowIdleWarning(false);
      return;
    }

    // Clocked in but no task running - start/continue idle tracking
    if (idleStartTimeRef.current === null) {
      idleStartTimeRef.current = Date.now();
    }
  }, [isClockedIn, hasRunningTask]);

  // Timer to update idle seconds
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isClockedIn && !hasRunningTask && idleStartTimeRef.current !== null) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - idleStartTimeRef.current!) / 1000);
        setIdleSeconds(elapsed);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isClockedIn, hasRunningTask]);

  // Check if we should show warning
  useEffect(() => {
    if (isClockedIn && !hasRunningTask && idleSeconds >= IDLE_WARNING_THRESHOLD) {
      if (!idleWarningSentRef.current) {
        idleWarningSentRef.current = true;
        setShowIdleWarning(true);
        sendIdleSlackNotification(Math.floor(idleSeconds / 60));
      }
    }
  }, [isClockedIn, hasRunningTask, idleSeconds, sendIdleSlackNotification]);

  const dismissWarning = useCallback(() => {
    setShowIdleWarning(false);
  }, []);

  return {
    showIdleWarning,
    dismissWarning,
    idleMinutes: Math.floor(idleSeconds / 60)
  };
};

export default useIdleWarning;

